import { useEffect } from 'react';
import Cookies from 'js-cookie';
import useStore from '@/store/useStore';
import { IS_PROD } from '@/utils/env';
import { BeCountriesAndCurrencies, GeCountriesAndCurrencies } from '@/data/geCountries';

const useGlobalEData = () => {
  const setLocation = useStore(state => state.setLocation);
  const { globalEUKOrAUFlag, globalESite, globalEFlag, ipCountry } = useStore(state => state.user);

  // Helper function to set the cookie
  const setGlobalECookie = (countryCode: string, currencyCode?: string, cultureCode?: string) => {
    const domain = IS_PROD ? '.brilliantearth.com' : `.${window.location.hostname.replace(/^www\./, '')}`;

    Cookies.set('GlobalE_Data', JSON.stringify({ countryISO: countryCode, currencyCode, cultureCode }), {
      domain,
      sameSite: 'None',
      secure: true,
      path: '/',
    });
  };

  function checkVisitor() {
    const visitedBefore = Cookies.get('visitedBefore');
    if (visitedBefore) {
      return false;
    }
    Cookies.set('visitedBefore', 'true', { expires: 365 });
    return true;
  }

  useEffect(() => {
    if (globalEFlag !== null && globalESite !== null) {
      const isNewUser = checkVisitor();
      const selCountry = Cookies.get('sel_country');
      const path = window.location.pathname.split('/')[1];
      const pathCountryMap = {
        'en-us': 'US',
        'en-ca': 'CA',
        'en-au': 'AU',
        'en-gb': 'GB',
      };

      if (!globalEFlag) {
        setLocation({ localGeToggle: true });
        return;
      }

      if (isNewUser) {
        const countryCode = pathCountryMap[path] || ipCountry;
        if (
          countryCode === 'US' ||
          countryCode === 'CA' ||
          (!globalEUKOrAUFlag && (countryCode === 'GB' || countryCode === 'AU'))
        ) {
          setLocation({ localGeToggle: true });
          Cookies.remove('GlobalE_Data', { domain: '.bglobale.com' });

          const country = BeCountriesAndCurrencies.find(c => c.CountryCode === countryCode);
          if (country) {
            setGlobalECookie(country.CountryCode, country.CurrencyCode, country.CultureProgId);
          }
        } else {
          setLocation({ localGeToggle: false });
          const country = GeCountriesAndCurrencies.find(c => c.CountryCode === countryCode);
          if (country) {
            setGlobalECookie(country.CountryCode, country.CurrencyCode, country.CultureProgId);
          }
        }
        return;
      }

      if (selCountry) {
        const countryCode = pathCountryMap[path] || selCountry;
        const country = GeCountriesAndCurrencies.find(c => c.CountryCode === countryCode);

        if (
          countryCode === 'US' ||
          countryCode === 'CA' ||
          (!globalEUKOrAUFlag && (countryCode === 'GB' || countryCode === 'AU'))
        ) {
          Cookies.remove('GlobalE_Data', { domain: '.bglobale.com' });
          setLocation({ localGeToggle: true });
        } else if (country) {
          setGlobalECookie(country.CountryCode, country.CurrencyCode, country.CultureProgId);
          setLocation({ localGeToggle: false });
        } else {
          Cookies.remove('GlobalE_Data', { domain: '.bglobale.com' });
          setLocation({ localGeToggle: true });
          setGlobalECookie(countryCode, 'USD');
        }
        return;
      }

      if (globalESite) {
        const countryCode = pathCountryMap[path] || ipCountry;
        if (
          countryCode === 'US' ||
          countryCode === 'CA' ||
          (!globalEUKOrAUFlag && (countryCode === 'GB' || countryCode === 'AU'))
        ) {
          Cookies.remove('GlobalE_Data', { domain: '.bglobale.com' });
          setLocation({ localGeToggle: true });
        } else {
          setLocation({ localGeToggle: false });
          const country = GeCountriesAndCurrencies.find(c => c.CountryCode === countryCode);
          if (country) {
            setGlobalECookie(country.CountryCode, country.CurrencyCode, country.CultureProgId);
          }
        }
      } else {
        Cookies.remove('GlobalE_Data', { domain: '.bglobale.com' });
        setLocation({ localGeToggle: true });
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalEFlag, globalESite]);
};

export default useGlobalEData;
